export const authoringSections = [
  {
    key: "items",
    label: "Items",
    description:
      "Design reusable question and activity templates that power assignments and lessons.",
    types: [
      {
        key: "mcma",
        navLabel: "MCMA",
        title: "Multiple Choice (Multiple Answer)",
        tagline: "Check-all-that-apply questions with flexible grading rules.",
        description:
          "Allow learners to select more than one correct response while providing rich rationales and feedback for each option.",
        highlights: [
          "Capture partial-credit scoring rules that match your grading policy.",
          "Define shuffle and locking behaviour for answer options.",
          "Attach teaching notes so facilitators know when to use the item.",
        ],
        actions: [
          { label: "Design MCMA template", variant: "contained" },
          { label: "Preview learner experience", variant: "outlined" },
        ],
      },
      {
        key: "markup",
        navLabel: "Markup",
        title: "Markup Highlight Interaction",
        tagline: "Let learners label, underline, or colour passages of text.",
        description:
          "Create markup tasks that invite learners to interact with authentic texts by highlighting evidence, labelling patterns, or marking grammatical features.",
        highlights: [
          "Define markup palettes with clear instructional intent.",
          "Surface exemplar rationales so facilitators can guide discussion.",
          "Capture analytics on what learners select for rapid review.",
        ],
        actions: [
          { label: "Author markup activity", variant: "contained" },
          { label: "Preview colour palette", variant: "outlined" },
        ],
      },
      {
        key: "mfb",
        navLabel: "MFB",
        title: "Multi Fill in the Blank",
        tagline: "Author cloze style questions with intelligent answer validation.",
        description:
          "Define prompts with multiple blanks, acceptable responses, and automated scoring guidance for facilitators.",
        highlights: [
          "Support synonyms and case sensitivity for each blank.",
          "Set per-blank feedback and remediation suggestions.",
          "Map standards and difficulty levels for reporting.",
        ],
        actions: [
          { label: "Create MFB template", variant: "contained" },
          { label: "Link reference material", variant: "outlined" },
        ],
      },
      {
        key: "true-false",
        navLabel: "True / False",
        title: "True or False",
        tagline: "Quick knowledge checks with immediate feedback.",
        description:
          "Capture binary concept checks that emphasise explanations and next steps when learners answer incorrectly.",
        highlights: [
          "Craft rationale and remediation for each outcome.",
          "Choose between instant or delayed feedback modes.",
          "Bundle related true/false items into item sets for reuse.",
        ],
        actions: [
          { label: "Author true/false item", variant: "contained" },
          { label: "Duplicate from library", variant: "outlined" },
        ],
      },
      {
        key: "drag-drop",
        navLabel: "Drag & Drop",
        title: "Drag and Drop Interaction",
        tagline: "Spatial reasoning or sequencing interactions for higher-order practice.",
        description:
          "Configure draggable tokens, drop zones, and scoring to support classification, ordering, and labelling tasks.",
        highlights: [
          "Upload custom imagery or diagrams for the interaction.",
          "Set tolerance values and accessibility alternatives.",
          "Document facilitation tips for group-based play.",
        ],
        actions: [
          { label: "Build drag & drop", variant: "contained" },
          { label: "Test accessibility flow", variant: "outlined" },
        ],
      },
      {
        key: "match",
        navLabel: "Match",
        title: "Matching Interaction",
        tagline: "Pair concepts, terms, or visuals to strengthen connections.",
        description:
          "Design matching tasks that help learners associate ideas, vocabulary, and representations across contexts.",
        highlights: [
          "Group prompts into sets with optional distractors.",
          "Author targeted feedback for correct and incorrect pairings.",
          "Control shuffling behaviour to support accessibility needs.",
        ],
        actions: [
          { label: "Create matching activity", variant: "contained" },
          { label: "Preview match flow", variant: "outlined" },
        ],
      },
      {
        key: "arrange",
        navLabel: "Arrange",
        title: "Arrange Sequence",
        tagline: "Order steps or ideas to assess process thinking.",
        description:
          "Author sequencing prompts where learners arrange items to demonstrate understanding of chronology or procedures.",
        highlights: [
          "Support vertical or horizontal sequencing layouts.",
          "Specify partial-credit scoring for near-correct orders.",
          "Document facilitation cues for discussion-based reviews.",
        ],
        actions: [
          { label: "Build arrange task", variant: "contained" },
          { label: "Review answer key", variant: "outlined" },
        ],
      },
      {
        key: "ranking",
        navLabel: "Ranking",
        title: "Ranking Activity",
        tagline: "Prioritise options to surface reasoning and justification.",
        description:
          "Create ranking activities that require learners to order choices based on criteria and explain their rationale.",
        highlights: [
          "Capture rubrics that evaluate reasoning quality.",
          "Enable reflection prompts after ranking is complete.",
          "Provide exemplar responses for facilitator reference.",
        ],
        actions: [
          { label: "Author ranking activity", variant: "contained" },
          { label: "Share discussion guide", variant: "outlined" },
        ],
      },
      {
        key: "iraab",
        navLabel: "Iraab",
        title: "Iraab Builder",
        tagline: "Support Arabic grammar analysis with structured prompts.",
        description:
          "Build Iraab items that guide learners through parsing Arabic sentences and identifying grammatical roles.",
        highlights: [
          "Provide sentence breakdowns with optional hints.",
          "Capture correct grammatical labels and explanations.",
          "Attach audio pronunciation and exemplar analyses.",
        ],
        actions: [
          { label: "Design Iraab item", variant: "contained" },
          { label: "Upload supporting resources", variant: "outlined" },
        ],
      },
    ],
  },
  {
    key: "lessons",
    label: "Lessons",
    description:
      "Plan cohesive lesson experiences that blend media, practice, and facilitation guidance.",
    types: [
      {
        key: "course-planner",
        navLabel: "Course Planner",
        title: "Guided lesson authoring",
        description:
          "Use the step-by-step workspace to choose a course, organise modules, and publish polished lessons for your learners.",
      },
    ],
  },
  {
    key: "enrollments",
    label: "Enrollments",
    description:
      "Monitor enrollment interest and follow up with prospective learners across cohorts.",
    types: [
      {
        key: "overview",
        navLabel: "Overview",
        title: "Enrollment pipeline",
        description:
          "Review enrollment submissions by cohort year, access applicant details, and coordinate timely follow up.",
        defaultYear: 2026,
      },
    ],
  },
  {
    key: "contacts",
    label: "Contacts",
    description:
      "Stay on top of inbound questions and partnership requests captured through the site.",
    types: [
      {
        key: "overview",
        navLabel: "Overview",
        title: "Contact requests",
        description:
          "Browse recent contact form submissions, review the message history, and respond to prospective learners or partners.",
      },
    ],
  },
  {
    key: "assets",
    label: "Assets",
    description:
      "Manage the media, documents, and shared resources that support learning experiences.",
    types: [
      {
        key: "media",
        navLabel: "Media Library",
        title: "Media Library",
        tagline: "Curate images, audio, and video for use across lessons.",
        description:
          "Organise multimedia assets with tagging, rights information, and accessibility transcripts.",
        highlights: [
          "Upload new media with automatic transcoding.",
          "Track licensing details and expiration dates.",
          "Generate captions and alternative text guidance.",
        ],
        actions: [
          { label: "Upload media", variant: "contained" },
          { label: "Review usage reports", variant: "outlined" },
        ],
      },
      {
        key: "documents",
        navLabel: "Documents",
        title: "Document Repository",
        tagline: "Version and share facilitator guides or worksheets.",
        description:
          "Store documents with check-in/out workflows, version history, and collaborative annotations.",
        highlights: [
          "Set approval steps before publishing updates.",
          "Track curriculum alignment metadata.",
          "Manage sharing permissions across teams.",
        ],
        actions: [
          { label: "Add document", variant: "contained" },
          { label: "View revision history", variant: "outlined" },
        ],
      },
      {
        key: "widgets",
        navLabel: "Interactive Widgets",
        title: "Interactive Widgets",
        tagline: "Reusable mini-apps that embed into lessons and assignments.",
        description:
          "Catalogue custom-built widgets, configure parameters, and publish usage documentation.",
        highlights: [
          "Document embed codes and configuration defaults.",
          "Collect feedback from facilitators and learners.",
          "Plan update cycles with engineering partners.",
        ],
        actions: [
          { label: "Register new widget", variant: "contained" },
          { label: "Share implementation guide", variant: "outlined" },
        ],
      },
    ],
  },
  {
    key: "assignments",
    label: "Assignments",
    description:
      "Assemble assignments that align with pacing guides and grading workflows.",
    types: [
      {
        key: "diagnostic",
        navLabel: "Diagnostic",
        title: "Diagnostic Assignment",
        tagline: "Gauge readiness before instruction begins.",
        description:
          "Curate items that surface prior knowledge and skill gaps so you can tailor instruction.",
        highlights: [
          "Tag each task with the skill strand it assesses.",
          "Set mastery thresholds that trigger recommendations.",
          "Coordinate make-up windows for absent learners.",
        ],
        actions: [
          { label: "Assemble diagnostic", variant: "contained" },
          { label: "Review data blueprint", variant: "outlined" },
        ],
      },
      {
        key: "practice",
        navLabel: "Practice",
        title: "Practice Assignment",
        tagline: "Provide distributed practice with targeted feedback.",
        description:
          "Mix auto-graded and instructor-reviewed items to reinforce recent learning targets.",
        highlights: [
          "Balance difficulty using your item metadata.",
          "Configure retry policies and feedback timing.",
          "Track completion expectations for each cohort.",
        ],
        actions: [
          { label: "Build practice set", variant: "contained" },
          { label: "Assign to classes", variant: "outlined" },
        ],
      },
      {
        key: "performance",
        navLabel: "Performance Task",
        title: "Performance Task",
        tagline: "Evaluate mastery through authentic demonstrations.",
        description:
          "Bundle rubrics, resources, and submission guidelines for deeper assessment experiences.",
        highlights: [
          "Attach rubric criteria and exemplars.",
          "Define milestones and checkpoints.",
          "Plan for peer and self assessment options.",
        ],
        actions: [
          { label: "Launch performance task", variant: "contained" },
          { label: "Share rubric", variant: "outlined" },
        ],
      },
    ],
  },
  {
    key: "taxonomy",
    label: "Taxonomy",
    description:
      "Maintain shared learning taxonomies that power search, reporting, and alignment workflows.",
    types: [
      {
        key: "arabic",
        navLabel: "Arabic",
        title: "Arabic taxonomy management",
        description:
          "Steward Arabic taxonomy records, keeping native-script terms aligned with their transliterations.",
      },
      {
        key: "english",
        navLabel: "English",
        title: "English taxonomy",
        description:
          "Manage taxonomy entries for English language content, including categories, strands, and usage guidance.",
      },
      {
        key: "science",
        navLabel: "Science",
        title: "Science taxonomy",
        description:
          "Organise subject-specific taxonomy entries that support science and STEM curricula.",
      },
    ],
  },
];

export const authoringSectionsByKey = authoringSections.reduce((acc, section) => {
  acc[section.key] = section;
  return acc;
}, {});
